import React from 'react';
import { CheckCircle, WarningCircle, XCircle, Plus, Truck } from '@phosphor-icons/react';
import { groupSelections, customerUnitPrice, priceCalcDescription, roundCents, roundPct1, computePro75MarginExample } from '../lib/bundleHelpers';
import { Pro75MarginExample } from './Pro75MarginExample';
import { useAuth } from '../auth/AuthContext';

const BADGE_CONFIGS = {
  valid: { icon: <CheckCircle size={20} weight="fill" />, color: 'bg-success', textColor: 'text-success', bgColor: 'bg-success/10' },
  incomplete: { icon: <WarningCircle size={20} weight="fill" />, color: 'bg-warning', textColor: 'text-warning', bgColor: 'bg-warning/10' },
  invalid: { icon: <XCircle size={20} weight="fill" />, color: 'bg-danger', textColor: 'text-danger', bgColor: 'bg-danger/10' },
};

export const ValidationBadge = ({ status, message }) => {
  const config = BADGE_CONFIGS[status] || BADGE_CONFIGS.incomplete;
  return (
    <div
      className={`flex items-center gap-2 px-4 py-3 rounded-lg ${config.bgColor} border border-${config.color}/20`}
      data-testid="validation-badge"
    >
      <span className={config.textColor}>{config.icon}</span>
      <span className={`text-sm font-medium ${config.textColor}`}>{message}</span>
    </div>
  );
};

const HOW_IT_WORKS_STEPS = [
  'Tick a tier — Box or Pallet — on a Core protein.',
  'Pick your flavours.',
  'Add supporting items from the other ranges — the status bar turns green when your saver is complete, then save it.',
];

const SelectedProductsList = ({ selectedProducts, productDiscounts, showCalc }) => {
  if (selectedProducts.length === 0) {
    return (
      <div data-testid="saver-how-it-works" className="space-y-2.5">
        {HOW_IT_WORKS_STEPS.map((step, i) => (
          <div key={step} className="flex items-start gap-2.5 text-sm text-text-secondary">
            <span className="shrink-0 w-5 h-5 rounded-full bg-gold/15 border border-gold/40 text-gold text-[11px] font-bold flex items-center justify-center">{i + 1}</span>
            <span className="leading-snug">{step}</span>
          </div>
        ))}
      </div>
    );
  }
  return (
    <>
      {groupSelections(selectedProducts).map((g) => {
        const unit = customerUnitPrice(g, productDiscounts);
        const desc = showCalc ? priceCalcDescription(g, productDiscounts) : null;
        return (
          <div
            key={`${g.id}-${g.selectedFlavour || 'default'}`}
            data-testid={`summary-row-${g.id}-${g.selectedFlavour || 'none'}`}
            className="text-sm text-text-primary"
          >
            <div className="flex justify-between gap-3">
              <span className="min-w-0">
                {g.name} {g.size && (g.sizeLabel ? `(${g.size} ${g.sizeLabel})` : `(${g.size})`)}
                {g.selectedFlavour && <span className="text-gold"> – {g.selectedFlavour}</span>}
                {g.qty > 1 && (
                  <span className="ml-1 text-gold font-semibold" data-testid={`summary-qty-${g.id}-${g.selectedFlavour || 'none'}`}>× {g.qty}</span>
                )}
              </span>
              <span className="text-text-secondary shrink-0">${(unit * g.qty).toFixed(2)}</span>
            </div>
            {showCalc && desc && (
              <div
                className="text-[10px] text-gold/70 italic mt-0.5 ml-1"
                data-testid={`summary-calc-${g.id}-${g.selectedFlavour || 'none'}`}
              >
                {desc.formula}{g.qty > 1 ? ` × ${g.qty} = $${(unit * g.qty).toFixed(2)}` : ''}
              </div>
            )}
          </div>
        );
      })}
    </>
  );
};

// Supporting-slots progress: gold gradient fill while filling, solid green when full.
// Quarter tick marks give the bar visual rhythm without cluttering large caps.
const SupportingProgress = ({ supportingCount, supportingCap }) => {
  const complete = supportingCap > 0 && supportingCount >= supportingCap;
  const remaining = Math.max(0, supportingCap - supportingCount);
  const pct = supportingCap > 0 ? Math.min(100, (supportingCount / supportingCap) * 100) : 0;
  return (
    <div className="mb-4" data-testid="supporting-progress">
      <div className="flex items-baseline justify-between mb-1.5">
        <span className="text-xs text-text-secondary">Supporting items</span>
        <span
          className={`text-xs font-semibold ${complete ? 'text-success' : 'text-warning'}`}
          data-testid="supporting-progress-count"
        >
          {supportingCount} / {supportingCap}{complete ? ' — full' : ` · ${remaining} to go`}
        </span>
      </div>
      <div className="relative h-3 rounded-full bg-black/60 border border-border-color overflow-hidden">
        <div
          data-testid="supporting-progress-fill"
          className={`relative h-full rounded-full transition-[width] duration-500 ease-out ${
            complete ? 'bg-success' : 'bg-gradient-to-r from-amber-700 via-yellow-500 to-gold'
          }`}
          style={{ width: `${pct}%` }}
        >
          {!complete && pct > 0 && (
            <span
              className="absolute inset-0 opacity-25 pointer-events-none"
              style={{ backgroundImage: 'repeating-linear-gradient(45deg, rgba(255,255,255,0.7) 0 5px, transparent 5px 10px)' }}
            />
          )}
        </div>
        {[25, 50, 75].map((t) => (
          <span key={t} className="absolute top-0 h-full w-px bg-black/50" style={{ left: `${t}%` }} />
        ))}
      </div>
    </div>
  );
};

// Nudge shown when a Box-tier saver is within one box of the Pallet Layer minimum.
// Includes a savings teaser (extra $ at pallet pricing on the CURRENT supporting
// items) and a one-click upgrade that keeps the whole selection.
const PalletProximityAlert = ({ coreCount, coreBoxSize, coreSize, extraSaving, onSwitchTier }) => {
  const palletMin = 5 * coreBoxSize;
  const tubsAway = palletMin - coreCount;
  return (
    <div
      data-testid="pallet-proximity-alert"
      className="mb-4 flex items-start gap-3 px-4 py-3 rounded-lg bg-gold/10 border border-gold/40"
    >
      <Truck size={22} weight="duotone" className="text-gold shrink-0 mt-0.5 animate-pulse" />
      <div className="text-sm min-w-0 flex-1">
        <p className="font-semibold text-gold">
          {tubsAway} tub{tubsAway === 1 ? '' : 's'} from a Pallet Layer
        </p>
        <p className="text-xs text-text-secondary mt-0.5 leading-snug">
          5 boxes ({palletMin} × {coreSize}) unlocks the Pallet Layer tier — pallet pricing and free
          supporting choice.
        </p>
        {extraSaving > 0 && (
          <p className="text-xs text-success font-semibold mt-1" data-testid="pallet-teaser-saving">
            Pallet pricing would save an extra ${extraSaving.toFixed(2)} on your current supporting
            items — and more as you fill the pallet.
          </p>
        )}
        {onSwitchTier && (
          <button
            type="button"
            onClick={() => onSwitchTier('pallet')}
            data-testid="switch-to-pallet-button"
            className="mt-2 px-3 py-1.5 rounded-lg bg-gold text-black text-xs font-bold hover:bg-gold-hover transition-colors"
          >
            Switch to Pallet Layer — keeps your selection
          </button>
        )}
      </div>
    </div>
  );
};

// Shown in Pallet mode while below the 5-box minimum: honest downgrade to Box
// tier. Keeps the selection intact but the Pallet discount is exchanged for the
// Box discount — we surface that trade-off explicitly so the user isn't
// surprised.
const PalletShortfallAlert = ({ coreCount, coreBoxSize, coreSize, palletPct, boxPct, supportingSubtotal, onSwitchTier }) => {
  const palletMin = 5 * coreBoxSize;
  const pPct = roundPct1(palletPct);
  const bPct = roundPct1(boxPct);
  const pctDrop = Math.max(0, pPct - bPct);
  const savingLost = pctDrop > 0
    ? roundCents(roundCents(supportingSubtotal) * pctDrop / 100)
    : 0;
  return (
    <div
      data-testid="pallet-shortfall-alert"
      className="mb-4 flex items-start gap-3 px-4 py-3 rounded-lg bg-warning/10 border border-warning/40"
    >
      <Truck size={22} weight="duotone" className="text-warning shrink-0 mt-0.5" />
      <div className="text-sm min-w-0 flex-1">
        <p className="font-semibold text-warning">
          {palletMin - coreCount} more tub{palletMin - coreCount === 1 ? '' : 's'} to fill the pallet
        </p>
        <p className="text-xs text-text-secondary mt-0.5 leading-snug">
          Pallet Layer needs {palletMin} × {coreSize} tubs (5 boxes) — you have {coreCount}. Keep
          adding to hold pallet pricing, or drop to Box tier below.
        </p>
        {pctDrop > 0 && (
          <p
            className="text-xs text-warning font-semibold mt-1"
            data-testid="pallet-downgrade-cost"
          >
            Dropping to Box swaps your {pPct}% pallet discount for the {bPct}% box discount
            {savingLost > 0 && (
              <>
                {' '}— about ${savingLost.toFixed(2)} less saving on your current supporting items.
              </>
            )}
          </p>
        )}
        {onSwitchTier && (
          <button
            type="button"
            onClick={() => onSwitchTier('box')}
            data-testid="switch-to-box-button"
            className="mt-2 px-3 py-1.5 rounded-lg border border-warning text-warning text-xs font-bold hover:bg-warning/10 transition-colors"
          >
            {pctDrop > 0
              ? `Drop to Box tier (${bPct}% discount)`
              : 'Drop to Box tier — selection kept'}
          </button>
        )}
      </div>
    </div>
  );
};

export const BundleSummaryCard = ({ selectedProducts, bundleName, setBundleName, bundleComments, setBundleComments, validation, onSave, discountPct, productDiscounts = {}, tier = 'single', palletPct = null, boxPct = null, onSwitchTier = null }) => {
  const { user } = useAuth();
  const isCustomer = user?.role === 'customer';
  const isValid = !!validation?.valid;
  // Saver pricing rule (Feb 2026):
  //   Core products → unit price unchanged (NO saver discount).
  //   Supporting products → saver-tier discount applied on top of unit price.
  let coreSubtotal = 0;
  let supportingSubtotal = 0;
  for (const p of selectedProducts) {
    const unit = customerUnitPrice(p, productDiscounts);
    if (p.range === 'Core') coreSubtotal += unit;
    else supportingSubtotal += unit;
  }
  const effectivePct = roundPct1(discountPct);
  const subtotal = roundCents(coreSubtotal + supportingSubtotal);
  const supportingDiscounted = roundCents(supportingSubtotal * (1 - effectivePct / 100));
  const bundlePrice = roundCents(coreSubtotal + supportingDiscounted);
  const customerSaving = roundCents(supportingSubtotal - supportingDiscounted);
  const savingPercent = subtotal > 0 ? (customerSaving / subtotal) * 100 : 0;
  const badgeStatus = validation?.status || (validation?.valid ? 'valid' : 'incomplete');
  const hasPerProductOverrides = selectedProducts.some((p) => typeof productDiscounts[p.id] === 'number');
  const coreItems = selectedProducts.filter((p) => p.range === 'Core');
  const coreSize = coreItems[0]?.size || '';
  const coreBoxSize = ['2kg', '1.8kg'].includes(coreSize) ? 6 : 8;
  const supportingCount = selectedProducts.length - coreItems.length;
  const supportingCap = coreItems.length * (coreBoxSize === 6 ? 2 : 1);
  const showPalletAlert = tier === 'box'
    && coreItems.length >= 4 * coreBoxSize
    && coreItems.length < 5 * coreBoxSize;
  const showPalletShortfall = tier === 'pallet'
    && coreItems.length > 0
    && coreItems.length < 5 * coreBoxSize;
  // Teaser: extra $ saved on the CURRENT supporting items at pallet pricing vs box.
  const palletExtraSaving = (typeof palletPct === 'number' && palletPct > effectivePct)
    ? roundCents(roundCents(supportingSubtotal) * (roundPct1(palletPct) - effectivePct) / 100)
    : 0;

  return (
    <div className="bundle-summary bg-surface border border-border-color rounded-xl p-6" data-testid="bundle-summary">
      <div className="flex items-center justify-between mb-4 gap-2">
        <h2 className="text-xl font-bold font-heading text-gold">Saver Summary</h2>
      </div>

      {coreItems.length > 0 && (
        <p className="text-xs text-text-secondary mb-3" data-testid="boxes-equivalent">
          {coreItems.length} × {coreSize} tub{coreItems.length > 1 ? 's' : ''} ={' '}
          <span className="text-gold font-semibold">{(coreItems.length / coreBoxSize).toFixed(1)} boxes</span>
          <span className="text-text-secondary"> ({coreBoxSize} tubs/box)</span>
        </p>
      )}

      {coreItems.length > 0 && (
        <SupportingProgress supportingCount={supportingCount} supportingCap={supportingCap} />
      )}

      {showPalletAlert && (
        <PalletProximityAlert
          coreCount={coreItems.length}
          coreBoxSize={coreBoxSize}
          coreSize={coreSize}
          extraSaving={palletExtraSaving}
          onSwitchTier={onSwitchTier}
        />
      )}

      {showPalletShortfall && (
        <PalletShortfallAlert
          coreCount={coreItems.length}
          coreBoxSize={coreBoxSize}
          coreSize={coreSize}
          palletPct={palletPct ?? discountPct}
          boxPct={boxPct ?? discountPct}
          supportingSubtotal={supportingSubtotal}
          onSwitchTier={onSwitchTier}
        />
      )}

      {validation && (
        <div className="mb-4">
          <ValidationBadge status={badgeStatus} message={validation.message} />
        </div>
      )}

      <div className="space-y-4 mb-6">
        <div>
          <p className="text-xs text-text-secondary mb-2">
            {selectedProducts.length === 0 ? 'How it works' : `Selected Products (${selectedProducts.length})`}
          </p>
          <div className="space-y-2">
            <SelectedProductsList
              selectedProducts={selectedProducts}
              productDiscounts={productDiscounts}
              showCalc={!isCustomer}
            />
          </div>
        </div>

        {selectedProducts.length > 0 && (
          <>
            {isValid && (
              <div className="border-t border-border-color pt-4">
                <div className="flex justify-between text-sm mb-1"><span className="text-text-secondary">Subtotal:</span><span className="text-text-primary font-semibold" data-testid="bundle-subtotal">${subtotal.toFixed(2)}</span></div>
                {!isCustomer && (
                  <p className="text-[10px] text-gold/70 italic mb-2" data-testid="bundle-subtotal-calc">
                    Core ${coreSubtotal.toFixed(2)} + Supporting ${supportingSubtotal.toFixed(2)}
                  </p>
                )}
                <div className="flex justify-between text-sm mb-1"><span className="text-text-secondary">Saver Price:</span><span className="text-gold font-semibold" data-testid="bundle-price">${bundlePrice.toFixed(2)}</span></div>
                {!isCustomer && (
                  <p className="text-[10px] text-gold/70 italic mb-2" data-testid="bundle-price-calc">
                    Core ${coreSubtotal.toFixed(2)} + Supporting ${supportingSubtotal.toFixed(2)} × (1 − {discountPct}%) = ${bundlePrice.toFixed(2)}
                  </p>
                )}
                <div className="flex justify-between text-sm"><span className="text-text-secondary">Customer Saving:</span><span className="text-success font-semibold" data-testid="bundle-saving">${customerSaving.toFixed(2)}{!isCustomer && ` (${savingPercent.toFixed(0)}%)`}</span></div>
                {!isCustomer && (
                  <p className="text-[10px] text-gold/70 italic mt-1" data-testid="bundle-saving-calc">
                    Supporting ${supportingSubtotal.toFixed(2)} × {discountPct}% = ${customerSaving.toFixed(2)} (no discount on Core)
                  </p>
                )}
                {!isCustomer && hasPerProductOverrides && (
                  <p className="text-[11px] text-text-secondary italic mt-2" data-testid="per-product-discount-note">
                    Includes customer-specific per-product discounts.
                  </p>
                )}
                <Pro75MarginExample
                  example={computePro75MarginExample(selectedProducts, customerSaving, productDiscounts)}
                  context="saver"
                />
              </div>
            )}
            <div className="border-t border-border-color pt-4 space-y-3">
              <div>
                <label className="text-sm text-text-secondary mb-2 block">Saver Name</label>
                <input
                  type="text"
                  value={bundleName}
                  onChange={(e) => setBundleName(e.target.value)}
                  data-testid="bundle-name-input"
                  placeholder="e.g., Complete Transformation Stack"
                  className="w-full px-3 py-2 bg-black border border-border-color rounded-lg text-text-primary focus:border-gold focus:outline-none"
                />
              </div>
              <div>
                <label className="text-sm text-text-secondary mb-2 block">Comments</label>
                <textarea
                  value={bundleComments}
                  onChange={(e) => setBundleComments(e.target.value)}
                  data-testid="bundle-comments-input"
                  placeholder="Add any notes or comments for this saver..."
                  rows={3}
                  className="w-full px-3 py-2 bg-black border border-border-color rounded-lg text-text-primary focus:border-gold focus:outline-none resize-y text-sm"
                />
              </div>
            </div>
            <button
              onClick={onSave}
              data-testid="save-bundle-button"
              disabled={!isValid}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gold text-black font-bold rounded-lg hover:bg-gold-hover disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Plus size={20} weight="bold" />
              Save to Saver List
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default BundleSummaryCard;
